import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getEventsByAdherent } from "../../../../services/eventServices";

import {
  getGroupAdherents,
  getGroupsByUser,
} from "../../../../services/groupServices";

import { useUser } from "../../../Home/UserContext";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_FILTERS,
  buildGlobalStats,
  buildJudokaSummaries,
  filterAdherents,
  flattenEventRows,
  getAdherentName,
  getGroupNames,
  getUserId,
  mergeApprovedAdherentsById,
} from "../utils/coachStatsUtils";

export function useCoachStatsPage() {
  const { user } = useUser();
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [eventsByAdherent, setEventsByAdherent] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const userId = useMemo(() => getUserId(user), [user]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchCoachAdherents = useCallback(async () => {
    if (!userId) return [];

    const userGroups = await getGroupsByUser(userId);
    const safeGroups = Array.isArray(userGroups) ? userGroups : [];

    setGroups(safeGroups);

    const adherentEntries = await Promise.all(
      safeGroups.map(async (group) => {
        try {
          const response = await getGroupAdherents(group.id);
          const groupAdherents = Array.isArray(response?.adherents)
            ? response.adherents
            : [];

          return groupAdherents
            .filter((adherent) => adherent.status === "approved")
            .map((adherent) => ({
              ...adherent,
              group,
            }));
        } catch (error) {
          console.error(
            `Erreur lors du chargement des adhérents du groupe ${group.id} :`,
            error,
          );

          return [];
        }
      }),
    );

    const mergedAdherents = mergeApprovedAdherentsById(adherentEntries.flat());

    setAdherents(mergedAdherents);

    return mergedAdherents;
  }, [userId]);

  const fetchEvents = useCallback(async (targetAdherents = []) => {
    const entries = await Promise.all(
      targetAdherents.map(async (adherent) => {
        try {
          const response = await getEventsByAdherent(adherent.id);
          return [
            adherent.id,
            Array.isArray(response?.Events) ? response.Events : [],
          ];
        } catch (error) {
          console.error(
            `Erreur lors du chargement des événements de l'adhérent ${adherent.id} :`,
            error,
          );

          return [adherent.id, []];
        }
      }),
    );

    const eventsMap = Object.fromEntries(entries);

    setEventsByAdherent(eventsMap);

    return eventsMap;
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (!userId) {
        setGroups([]);
        setAdherents([]);
        setEventsByAdherent({});
        setLoading(false);
        return;
      }

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const coachAdherents = await fetchCoachAdherents();
        await fetchEvents(coachAdherents);

        if (!silent && !isInitialLoad) {
          notificationRef.current?.("Statistiques actualisées.", "success");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des statistiques :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des statistiques.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchCoachAdherents, fetchEvents, userId],
  );

  useEffect(() => {
    fetchData({ silent: true });
  }, [fetchData]);

  const filteredAdherents = useMemo(() => {
    return filterAdherents(adherents, filters);
  }, [adherents, filters]);

  const filteredRows = useMemo(() => {
    return flattenEventRows(filteredAdherents, eventsByAdherent);
  }, [eventsByAdherent, filteredAdherents]);

  const stats = useMemo(() => {
    return buildGlobalStats(filteredRows, filteredAdherents);
  }, [filteredAdherents, filteredRows]);

  const judokaSummaries = useMemo(() => {
    return buildJudokaSummaries(filteredAdherents, filteredRows);
  }, [filteredAdherents, filteredRows]);

  const scopeLabel = useMemo(() => {
    if (filters.adherentId) {
      const adherent = adherents.find(
        (item) => String(item.id) === String(filters.adherentId),
      );

      return adherent ? getAdherentName(adherent) : "Judoka sélectionné";
    }

    if (filters.groupId) {
      const group = groups.find(
        (item) => String(item.id) === String(filters.groupId),
      );

      return group ? group.nom : "Groupe sélectionné";
    }

    return "Tous les judokas";
  }, [adherents, filters.adherentId, filters.groupId, groups]);

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "adherentId" && value) {
        next.groupId = "";
      }

      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return {
    groups,
    adherents,
    filters,
    stats,
    judokaSummaries,
    filteredRows,
    scopeLabel,
    loading,
    refreshing,
    fetchData,
    updateFilter,
    resetFilters,
  };
}